import React, { useState } from 'react';
import {
  MultiSelect,
  BooleanOption,
  Value,
  ValueVariant,
  OptionComponent,
  ValueComponent,
} from 'app/components/forms/MultiSelect';
import { useI18n, tFunctionType } from 'app/components/localization';
import { List, Record } from 'immutable';
import { fuzzyFilterList } from 'app/utils/filtering';
import { ExtractProps } from 'app/utils/types';

interface IProps {
  onSelect: (option: string, value: boolean | undefined) => unknown;
  onRemove: (option: string) => unknown;
  values: Record<{[key: string]: boolean | undefined}>;
  filters: List<IFilter>;
  names: {[key: string]: { getName: (t: tFunctionType) => string }};

  placeholder: string;
  noResults: string;
  closeOnAdd?: boolean;
}

interface IFilter {
  id: string;
  getName: (t: tFunctionType) => string;
}

interface IValue {
  value: boolean | undefined;
  id: string;
}

function isBooleanOrUndefined(val: unknown): val is boolean | undefined {
  return typeof val === 'boolean' || val === undefined;
}

/**
 * Helper for converting a record defined with an enum type into a more generic
 * record used by the BooleanMultiSelect
 * @param value - The more specific record to convert
 */
export const makeBooleanValues = <T, >(
  value: Record<T>,
): Record<{[key: string]: boolean | undefined}> => {
  const BooleanOptions = Record<{[key: string]: boolean | undefined}>(
    value.toSeq().reduce((acc: {[k: string]: boolean | undefined }, v, k): {[k: string]: boolean | undefined } => {
      const newAcc = {
        ...acc,
      };
      if (typeof k === 'string' && isBooleanOrUndefined(v)) {
        newAcc[k] = v;
      } else {
        throw new Error('BooleanValues must be keyed by string and must only index boolean or undefined values');
      }
      return newAcc;
    }, {}),
  );
  const r = new BooleanOptions();
  return r;
};


// Create a { id, value } pair for the options.
const getOptions = (
  values: Record<{[key: string]: boolean | undefined}>,
  query: string,
  t: tFunctionType,
  filters: List<IFilter>,
) => (
    fuzzyFilterList({
      getItemText: (status) => status.getName(t),
      filter: query,
      list: filters,
    })
    .map(status => ({
      id: status.id,
      value: values.get(status.id),
    }))
);

  // Convert values to a { id, value } pair and filter undefined values.
const getValues = (values: Record<{[key: string]: boolean | undefined }>): List<IValue> => (
  values
    .toSeq()
    .filter((value) => value !== undefined)
    .map((value, id) => ({ id: `${id}`, value }))
    .toList()
);

const useIdAsKey = ({ id }: { id: string }) => id;

const BooleanOptionMultiSelect = ({
  onSelect,
  onRemove,
  values,
  names,
  filters,
  ...rest
}: IProps) => {
  const [query, setQuery] = useState('');
  const { t } = useI18n();
  const renderOption = ({ option, ...props }: ExtractProps<OptionComponent<IValue>>) => (
    <BooleanOption {...props} option={option}>{names[option.id].getName(t)}</BooleanOption>
  );
  const renderValue = ({ value, ...props }: ExtractProps<ValueComponent<IValue>>) => {
    const variant = value.value === false ? ValueVariant.Negative : ValueVariant.Positive;
    return (
      <Value {...props} value={value} variant={variant}>{names[value.id].getName(t)}</Value>
    );
  };

  return (
    <MultiSelect
      getOptionsKey={useIdAsKey}
      getValuesKey={useIdAsKey}
      query={query}
      options={getOptions(values, query, t, filters)}
      optionsComponent={renderOption}
      values={getValues(values)}
      valuesComponent={renderValue}
      onQueryChange={setQuery}
      onSelect={({ id, value }) => onSelect(id, value)}
      onClose={() => setQuery('')}
      onRemove={({ id }) => onRemove(id)}
      {...rest}
    />
  );
};

export default BooleanOptionMultiSelect;
